/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {ErrorType, getMode, getSearchParams, ResponseData, showDialog} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useState} from 'react';
import {AppContext} from "../../misc/appContext";
import {authorizeBolOAuth} from "../../../util/requestSender";
import ApiKey from "../../../models/apiKeyModel";
import BolProduct from "../../../models/bolProductModel";
import Account from "../../../models/accountModel";
import {createErrorMessage} from "../../../util/utils";
import {APIValidationStatus} from "../../../util/interfaces";
import {onConnectBolOauth} from "../../../util/bolOauthUtil";
import BolOauth from "./bolOauth";
import {getFromLocal} from "../../../util/storageUtil";
import {SELECTED_ACCOUNT_KEY} from "../../../util/constants";
import {useNavigate} from 'react-router';

export default function BolOauthController() {
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const [status, setStatus] = useState<APIValidationStatus>(APIValidationStatus.VALIDATING);
    const [error, setError] = useState<ErrorType>(null);

    function onClose() {
        navigate('/facturen');
    }

    function onRetry(evt: React.MouseEvent) {
        onConnectBolOauth(evt.shiftKey);
    }

    function mount() {
        const params = getSearchParams(window.location.search, true);
        if (params.has('error-description')) {
            setError(params.get('error-description'))
            setStatus(APIValidationStatus.ERROR);
        } else if (params.has('code') || getMode() !== 'prod') {
            const code: string = params.get('code') ?? '';
            setStatus(APIValidationStatus.VALIDATING);
            setError(null);
            authorizeCode(code);
        } else {
            setStatus(APIValidationStatus.ERROR);
            setError('We hebben geen autorisatiecode ontvangen.');
        }
    }

    function authorizeCode(code: string) {
        let accountId = getFromLocal(SELECTED_ACCOUNT_KEY);
        if (!accountId) {
            const {account} = context.accountContext;
            if (!account) return;
            accountId = account.id;
        }
        authorizeBolOAuth({
            data: {code, account_id: accountId}, callback: (data: ResponseData) => {
                if (data.success) {
                    const apiKey = new ApiKey(data.api_key);
                    if (data.is_new) {
                        navigate('/koppeling-geslaagd');
                        importProducts(apiKey);
                    }
                    else setStatus(APIValidationStatus.VALIDATED);
                } else {
                    setStatus(APIValidationStatus.ERROR);
                    setError(data.message ?? 'Het verbinden met de API van Bol is niet gelukt.')
                }
            }
        });
    }

    function importProducts(apiKey: ApiKey) {
        BolProduct.importAsync(apiKey.accountId, false, (account: Account | null, success: boolean, data: ResponseData) => {
            if (success) {
                setStatus(APIValidationStatus.VALIDATED);
                context.onChangeContext({account});
            } else
                showDialog(data.message ?? createErrorMessage('De verbinding met de API kan succesvol gelegd worden, ' +
                    'maar er is iets misgegaan bij het opslaan van de connectie.'), 'Opslaan mislukt')
        });
    }

    useEffect(() => {
        mount();
    }, []);

    return (
        <BolOauth onClose={onClose} onRetry={onRetry} status={status} error={error}/>
    );
}